import { FormattedMessage, useIntl } from 'react-intl';
import type { UseFormReturn } from 'react-hook-form';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import FormLabel from '@rio-cloud/rio-uikit/FormLabel';

import { getTrackingAttributes, TRACKING_CATEGORIES } from '../../configuration/setup/googleAnalytics';
import type { FormEmailData } from './CreatePartnerDialog';
import { isValidEmail } from '../../utils/utils';
import EmailContent from './EmailContent';
import { useAppSelector } from '../../configuration/setup/hooks';
import { getUserProfile } from '../../configuration/login/loginSlice';
import { useFetchAccountProfileQuery, useFetchPartnerConnectionTypesQuery } from '../../services/partnerNetworkApi';
import type { ConnectionType } from '../../services/partnerNetwork.types';

type CreateStepTwoProps = {
    form: UseFormReturn<FormEmailData>;
    connectionType: ConnectionType;
};

const CreateStepTwo = ({ form, connectionType }: CreateStepTwoProps) => {
    const { formState, register, getFieldState, watch } = form;

    const intl = useIntl();

    const accountId = useAppSelector(getUserProfile)?.account;
    const DEFAULT_LOCALE = 'de-DE';
    const locale = useAppSelector(getUserProfile)?.locale ?? DEFAULT_LOCALE;

    const { data: accountProfile } = useFetchAccountProfileQuery(accountId, {
        skip: !accountId,
    });
    const { data: partnerConnectionTypes, isLoading: isLoadingPartnerConnectionTypes } =
        useFetchPartnerConnectionTypesQuery();

    const name = watch('name');

    const { error: emailAddressError } = getFieldState('emailAddress', formState);

    const partnerConnectionType = partnerConnectionTypes?.items.find(item => item.connectionType === connectionType);
    const benefits = partnerConnectionType?.connectionFeatures[locale] ?? partnerConnectionType?.connectionFeatures.en;

    if (isLoadingPartnerConnectionTypes) {
        return <Spinner />;
    }

    return (
        <div
            className='position-relative width-100pct'
            {...getTrackingAttributes({
                trigger: 'visibility',
                category: TRACKING_CATEGORIES.OVERVIEW,
                action: 'Add partner dialog step two shown',
                label: 'Add partner dialog step two shown',
            })}
        >
            <div className='padding-bottom-20 text-color-darker'>
                <FormattedMessage id='intl-msg:network.invitationLink.validityPhrase' />
            </div>
            <form>
                <div className={`form-group ${emailAddressError ? 'has-feedback has-error' : ''}`}>
                    <FormLabel className='control-label' htmlFor='emailAddress'>
                        <FormattedMessage id='intl-msg:network.invitationLink.inviteeEmailAddress' />*
                    </FormLabel>
                    <div className='input-group'>
                        <span className='input-group-addon'>
                            <span className='uikit-icon icon-envelope' />
                        </span>
                        <input
                            type='text'
                            id='emailAddress'
                            className='form-control'
                            placeholder={intl.formatMessage({
                                id: 'intl-msg:network.placeholder.email',
                            })}
                            {...register('emailAddress', {
                                required: true,
                                validate: isValidEmail,
                            })}
                        />
                    </div>
                    {emailAddressError && (
                        <>
                            <span className='form-control-feedback uikit-icon icon-error-sign' />
                            <span className='help-block'>
                                <FormattedMessage id='intl-msg:network.error.validEmailAddressRequired' />
                            </span>
                        </>
                    )}
                </div>
                <div className='form-group'>
                    <FormLabel className='control-label' htmlFor='placeholder'>
                        <FormattedMessage id='intl-msg:network.invitationLink.inviteeName' />
                    </FormLabel>
                    <div className='input-group'>
                        <span className='input-group-addon'>
                            <span className='uikit-icon icon-user' />
                        </span>
                        <input
                            type='text'
                            id='placeholder'
                            className='form-control'
                            placeholder={intl.formatMessage({
                                id: 'intl-msg:network.placeholder.name',
                            })}
                            {...register('name', {
                                required: false,
                            })}
                        />
                    </div>
                </div>

                <div className='form-group'>
                    <FormLabel className='control-label' htmlFor='emailContent'>
                        <FormattedMessage id='intl-msg:network.invitationLink.emailPreview' />
                    </FormLabel>
                    <div
                        className='text-size-11 border border-style-dashed padding-5 pointer-events-none bg-decent rounded'
                        id='emailContent'
                    >
                        <EmailContent
                            requesterName={accountProfile?.displayName ?? ''}
                            inviteeName={name}
                            benefits={benefits}
                            locale={locale}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CreateStepTwo;
